import { Icon, TriangleAlert } from "@norican/icons";
import { Typography } from "@norican/react";
import { Link, Outlet } from "react-router";

import { useT } from "@/i18n";
import { useKiosk } from "@/lib/api";

import { AppHeader } from "./app-header";
import { PageTimeTracker } from "./page-time-tracker";

/**
 * The authenticated application frame.
 *
 * Used as a React Router layout route inside `<ProtectedRoute>`: it renders the
 * header once and the matched page into `<Outlet>`, so navigating between Ask,
 * and My tickets never remounts the shell.
 *
 * The skip link is the first focusable element on the page — a keyboard user
 * should not have to tab through the whole header on every route to reach the
 * content. `tabIndex={-1}` on `<main>` makes it a valid target for that jump
 * without putting it in the tab order.
 */
export function AppShell() {
  const { t } = useT();
  const { t: tt } = useT("tickets");
  const kiosk = useKiosk();

  return (
    <div className="app-surface text-fg flex min-h-screen flex-col">
      <PageTimeTracker />
      <a
        href="#main-content"
        className="bg-accent text-accent-fg focus-visible:ring-accent sr-only rounded-md px-4 py-2 focus-visible:not-sr-only focus-visible:absolute focus-visible:top-2 focus-visible:left-2 focus-visible:z-[var(--norican-z-toast)] focus-visible:ring-2"
      >
        {t("nav.skipToContent")}
      </a>

      <AppHeader />

      <main id="main-content" tabIndex={-1} className="flex-1 outline-none">
        <Outlet />
      </main>

      {/* A footer that earns its line: what this is, and the two places a
          confused employee might actually want.

          Deliberately no privacy or terms links. The Teams manifest references
          `${PORTAL_URL}/privacy` and `/terms`, but neither route exists in this
          app — linking them would send people to the Not Found page, which is
          worse than not offering them. Add them here when the pages exist. */}
      <footer className="border-border mt-auto border-t">
        <div className="mx-auto flex w-full max-w-[var(--norican-size-container-xl)] flex-wrap items-center justify-between gap-x-6 gap-y-3 px-4 py-6 sm:px-6 lg:px-8">
          <div className="flex min-w-0 flex-col gap-0.5">
            <Typography as="span" variant="body-sm" className="text-fg font-medium">
              {t("app.name")}
            </Typography>
            <Typography as="span" variant="caption" tone="muted">
              {t("app.tagline")}
            </Typography>
          </div>

          <nav aria-label={t("footer.label")} className="flex items-center gap-x-5 gap-y-2">
            {/* No ticket list on a shared device (1 Oct). */}
            {kiosk === null ? (
              <Link
                to="/tickets"
                className="text-accent-fg-subtle text-sm font-medium underline-offset-2 hover:underline"
              >
                {t("nav.tickets")}
              </Link>
            ) : null}
            {/* Help is not a destination of its own: it is a notice in the
                Message Center — placed, ranked and hideable like any other — so
                the footer points at the Message Center. */}
            <Link
              to="/messages"
              className="text-accent-fg-subtle text-sm font-medium underline-offset-2 hover:underline"
            >
              {t("footer.messages")}
            </Link>
            {/* Deliberately last and not styled as a call to action. Findable
                when someone is looking for a way to reach a person; not an
                invitation the rest of the time. */}
            <Link
              to="/emergency"
              className="text-danger-fg inline-flex items-center gap-1.5 text-sm underline-offset-2 hover:underline"
            >
              <Icon icon={TriangleAlert} size="xs" />
              {tt("emergency.footerLink")}
            </Link>
            <Typography as="span" variant="caption" tone="muted">
              {t("footer.rights")}
            </Typography>
          </nav>
        </div>
      </footer>
    </div>
  );
}
